import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Loader2, Calendar, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTenants } from '@/hooks/useTenants';
import { useDataCollectionTasks } from '@/hooks/useDataCollection';
import { useCreateSchedule, useUpdateSchedule } from '@/hooks/useScheduling';
import {
  Schedule,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  TIMEZONE_OPTIONS,
  validateCron
} from '@/services/api/scheduling';
import { CronBuilder } from './CronBuilder';
import { cn } from '@/lib/utils';

interface ScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: Schedule | null;
  defaultTenantId?: string;
}

const OEM_OPTIONS = [
  { value: '__auto__', label: 'Auto-detect from task' },
  { value: 'ford', label: 'Ford' },
  { value: 'stellantis', label: 'Stellantis' },
  { value: 'hyundai', label: 'Hyundai' }
];

const SAMPLING_OPTIONS = [
  { value: 'all', label: 'All VINs' },
  { value: 'random', label: 'Random sample' },
  { value: 'first', label: 'First N' },
  { value: 'last', label: 'Last N' }
];

export const ScheduleFormDialog: React.FC<ScheduleFormDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  defaultTenantId
}) => {
  const { toast } = useToast();
  const isEditing = !!schedule;

  // Form state
  const [tenantId, setTenantId] = useState(schedule?.tenant_id || defaultTenantId || '');
  const [taskName, setTaskName] = useState(schedule?.task_name || '');
  const [cronExpression, setCronExpression] = useState(schedule?.cron_expression || '0 6 * * 1');
  const [timeZone, setTimeZone] = useState(schedule?.time_zone || 'America/New_York');
  const [description, setDescription] = useState(schedule?.description || '');
  const [isEnabled, setIsEnabled] = useState(schedule?.is_enabled ?? true);
  const [autoTriggerVIN, setAutoTriggerVIN] = useState(
    schedule?.auto_trigger_vin_processing ?? true
  );
  const [vinOem, setVinOem] = useState(schedule?.vin_processing_config?.oem || '__auto__');
  const [vinSamplingStrategy, setVinSamplingStrategy] = useState<
    'all' | 'random' | 'first' | 'last'
  >(schedule?.vin_processing_config?.sampling_strategy || 'all');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Data fetching
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const { data: tasks, isLoading: tasksLoading } = useDataCollectionTasks(tenantId || null);

  // Mutations
  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when schedule changes
  useEffect(() => {
    if (schedule) {
      setTenantId(schedule.tenant_id);
      setTaskName(schedule.task_name);
      setCronExpression(schedule.cron_expression);
      setTimeZone(schedule.time_zone);
      setDescription(schedule.description || '');
      setIsEnabled(schedule.is_enabled);
      setAutoTriggerVIN(schedule.auto_trigger_vin_processing);
      setVinOem(schedule.vin_processing_config?.oem || '__auto__');
      setVinSamplingStrategy(schedule.vin_processing_config?.sampling_strategy || 'all');
    } else {
      setTenantId(defaultTenantId || '');
      setTaskName('');
      setCronExpression('0 6 * * 1');
      setTimeZone('America/New_York');
      setDescription('');
      setIsEnabled(true);
      setAutoTriggerVIN(true);
      setVinOem('__auto__');
      setVinSamplingStrategy('all');
    }
    setErrors({});
  }, [schedule, defaultTenantId, open]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!tenantId) {
      newErrors.tenantId = 'Tenant is required';
    }

    if (!taskName) {
      newErrors.taskName = 'Task is required';
    }

    const cronValidation = validateCron(cronExpression);
    if (!cronValidation.valid) {
      newErrors.cronExpression = cronValidation.error || 'Invalid cron expression';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validate()) return;

    // Close dialog immediately for instant feedback
    onOpenChange(false);

    // Show success toast immediately
    toast({
      title: isEditing ? 'Schedule Updated' : 'Schedule Created',
      description: isEditing
        ? 'Your schedule has been updated'
        : 'Your new schedule has been created'
    });

    // Build request data - convert '__auto__' sentinel back to undefined
    const actualVinOem = vinOem === '__auto__' ? undefined : vinOem;
    const vinProcessingConfig = autoTriggerVIN
      ? {
          oem: actualVinOem,
          sampling_strategy: vinSamplingStrategy
        }
      : undefined;

    if (isEditing && schedule) {
      const updateData: ScheduleUpdateRequest = {
        cron_expression: cronExpression,
        time_zone: timeZone,
        description: description || undefined,
        auto_trigger_vin_processing: autoTriggerVIN,
        vin_processing_config: vinProcessingConfig,
        is_enabled: isEnabled
      };

      updateMutation.mutate(
        { id: schedule.id, data: updateData },
        {
          onError: error => {
            toast({
              title: 'Failed to update schedule',
              description: error instanceof Error ? error.message : 'Unknown error',
              variant: 'destructive'
            });
          }
        }
      );
    } else {
      const createData: ScheduleCreateRequest = {
        task_name: taskName,
        tenant_id: tenantId,
        cron_expression: cronExpression,
        time_zone: timeZone,
        description: description || undefined,
        auto_trigger_vin_processing: autoTriggerVIN,
        vin_processing_config: vinProcessingConfig,
        is_enabled: isEnabled
      };

      createMutation.mutate(createData, {
        onError: error => {
          toast({
            title: 'Failed to create schedule',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive'
          });
        }
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Calendar className='h-5 w-5' />
            {isEditing ? 'Edit Schedule' : 'Create Schedule'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modify the schedule configuration'
              : 'Set up automatic data collection on a recurring schedule'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Tenant Selection */}
          <div className='space-y-2'>
            <Label htmlFor='tenant'>Tenant *</Label>
            <Select
              value={tenantId}
              onValueChange={val => {
                setTenantId(val);
                setTaskName(''); // Reset task when tenant changes
              }}
              disabled={isEditing}
            >
              <SelectTrigger className={cn(errors.tenantId && 'border-destructive')}>
                <SelectValue placeholder='Select a tenant...' />
              </SelectTrigger>
              <SelectContent>
                {tenantsLoading ? (
                  <SelectItem value='__loading__' disabled>
                    Loading...
                  </SelectItem>
                ) : (
                  tenants?.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {errors.tenantId && <p className='text-sm text-destructive'>{errors.tenantId}</p>}
          </div>

          {/* Task Selection */}
          <div className='space-y-2'>
            <Label htmlFor='task'>Task *</Label>
            <Select value={taskName} onValueChange={setTaskName} disabled={!tenantId || isEditing}>
              <SelectTrigger className={cn(errors.taskName && 'border-destructive')}>
                <SelectValue placeholder='Select a task...' />
              </SelectTrigger>
              <SelectContent>
                {tasksLoading ? (
                  <SelectItem value='__loading__' disabled>
                    Loading tasks...
                  </SelectItem>
                ) : !tenantId ? (
                  <SelectItem value='__select_tenant__' disabled>
                    Select a tenant first
                  </SelectItem>
                ) : tasks && tasks.length > 0 ? (
                  tasks.map(task => (
                    <SelectItem key={task.task_id} value={task.name}>
                      <div className='flex flex-col'>
                        <span>{task.name}</span>
                        <span className='text-xs text-muted-foreground'>
                          {task.oem} - {task.zone}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value='__no_tasks__' disabled>
                    No tasks available for this tenant
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.taskName && <p className='text-sm text-destructive'>{errors.taskName}</p>}
          </div>

          {/* Schedule (Cron Builder) */}
          <div className='space-y-2'>
            <Label>Schedule *</Label>
            <CronBuilder
              value={cronExpression}
              onChange={setCronExpression}
              error={errors.cronExpression}
            />
          </div>

          {/* Timezone */}
          <div className='space-y-2'>
            <Label htmlFor='timezone'>Timezone</Label>
            <Select value={timeZone} onValueChange={setTimeZone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='e.g., Weekly inventory refresh for Elder Ford Tampa'
              rows={2}
            />
          </div>

          {/* Auto VIN Processing Toggle */}
          <div className='flex items-center justify-between rounded-lg border p-4'>
            <div className='space-y-0.5'>
              <div className='flex items-center gap-2'>
                <Zap className='h-4 w-4 text-amber-500' />
                <Label className='font-medium'>Auto VIN Processing</Label>
              </div>
              <p className='text-sm text-muted-foreground'>
                Automatically trigger VIN processing after data collection completes
              </p>
            </div>
            <Switch checked={autoTriggerVIN} onCheckedChange={setAutoTriggerVIN} />
          </div>

          {/* VIN Processing Config (conditional) */}
          {autoTriggerVIN && (
            <div className='space-y-4 rounded-lg border bg-muted/30 p-4'>
              <h4 className='font-medium text-sm'>VIN Processing Configuration</h4>

              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label>OEM</Label>
                  <Select value={vinOem} onValueChange={setVinOem}>
                    <SelectTrigger>
                      <SelectValue placeholder='Auto-detect' />
                    </SelectTrigger>
                    <SelectContent>
                      {OEM_OPTIONS.map(oem => (
                        <SelectItem key={oem.value} value={oem.value}>
                          {oem.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label>Sampling Strategy</Label>
                  <Select
                    value={vinSamplingStrategy}
                    onValueChange={(v: 'all' | 'random' | 'first' | 'last') =>
                      setVinSamplingStrategy(v)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SAMPLING_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Enabled Toggle */}
          <div className='flex items-center justify-between rounded-lg border p-4'>
            <div className='space-y-0.5'>
              <Label className='font-medium'>Enable Schedule</Label>
              <p className='text-sm text-muted-foreground'>
                {isEnabled
                  ? 'Schedule is active and will run automatically'
                  : 'Schedule is paused and will not run'}
              </p>
            </div>
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Saving...
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Create Schedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleFormDialog;
